<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>
    <b-card>
      <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">會員管理</h4>

      <div class="row mx-0 justify-content-between mb-2 mt-4">
        <div>
          <b-button v-b-toggle="['collapse-filter', 'collapse-search']" variant="outline-info"><span class="mdi mdi-magnify"></span>進階搜尋</b-button>
        </div>
        <div>
          <router-link
            v-if="$permissions.has($permissions.consts.CUSTOMER_LIST_IMPORT)"
            :to="{
              name: 'CustomerListImport',
              query: { merchant_hashid: query.merchant_hashid },
            }"
            class="btn flex-shrink-0 mb-2 ml-2 btn-inverse-info"
            style="line-height: 20px;"
            target="_self"
          >
            <span class="mdi mdi-cloud-upload"></span> 批次上傳
          </router-link>
          <b-dropdown class="flex-shrink-0 mb-2 ml-2" variant="inverse-info">
            <template #button-content>
              <span class="mdi mdi-download"></span> 資料匯出
            </template>
            <b-dropdown-item @click="exportCustomers('excel')"
              >會員資料CSV</b-dropdown-item
            >
            <b-dropdown-item @click="exportCustomers('csv')"
              >標準資料CSV</b-dropdown-item
            >
          </b-dropdown>
        </div>
      </div>

      <div class="mb-2">
        <b-collapse id="collapse-filter" class="mt-2">
          <b-card>
             <b-form inline>
              <b-form-group
                label="來源通路"
                class="mr-2"
              >
                <b-form-select
                  v-model="query.merchant_hashid"
                  :options="merchants"
                  @change="handleChangeMerchant"
                  :disabled="merchants.length <= 1"
                ></b-form-select>
              </b-form-group>
              <b-form-group
                label="是否綁定會員"
              >
                <b-form-select
                  v-if="merchants.length > 1"
                  v-model="query.is_binded"
                  :options="bindedOptions"
                  @change="handleChangeMerchant"
                  :disabled="query.merchant_hashid === 'cross'"
                ></b-form-select>
              </b-form-group>
            </b-form>
          </b-card>
        </b-collapse>
        <b-collapse id="collapse-search" class="mt-2">
          <b-card>
            <b-input-group class="" style="max-width: 500px;">
              <b-input-group-prepend>
                <b-form-select
                  class="mr-2"
                  v-model="query.search_field"
                  :options="searchFieldOptions"
                  style="min-width: 120px;"
                ></b-form-select>
              </b-input-group-prepend>
              <b-form-input
                v-model="query.keyword"
                @keyup.enter="handlePageReset"
                :placeholder="searchPlaceholder"
                style="height: 38px;"
              ></b-form-input>
              <b-input-group-append>
                <b-button @click="handlePageReset"
                  ><i class="fa fa-search"></i
                ></b-button>
              </b-input-group-append>
            </b-input-group>
          </b-card>
        </b-collapse>
      </div>

      <div class="row">
        <div class="col-12">
          <b-table
            striped
            hover
            responsive
            :items="customers"
            :fields="fields"
            :busy="isFetchCustomerList"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle"></b-spinner>
              </div>
            </template>
            <template #cell(avatar_url)="data">
              <div class="d-flex align-items-center justify-content-center">
                <router-link
                  :to="{
                    name: 'CustomerDetailView',
                    params: { customerID: data.item.id },
                  }"
                >
                  <b-avatar
                    :src="data.item.avatar_url"
                    variant="secondary"
                    size="2rem"
                  ></b-avatar>
                </router-link>
              </div>
            </template>
            <template #cell(name)="data">
              <router-link
                :to="{
                  name: 'CustomerDetailView',
                  params: { customerID: data.item.id },
                }"
              >
                {{ hiddenString(data.item.name, 20) || "(無法辨識)" }}
              </router-link>
            </template>
            <template #cell(merchant_types)="data">
              <template v-if="query.merchant_hashid === 'cross'">
                <div :id="`${data.item.id}-tooltip`">
                  <b-badge
                    v-if="data.item.merchant_type_name"
                    variant="primary"
                    class="mr-1"
                    >{{ data.item.merchant_type_name.name }}</b-badge
                  >
                  <div
                    v-for="merchant in data.item.cross_merchants"
                    :key="merchant.id"
                  >
                    <br />
                    <b-badge variant="primary" class="mr-1">{{
                      merchant.merchant_type_name
                    }}</b-badge>
                  </div>
                </div>
                <b-popover
                  :target="`${data.item.id}-tooltip`"
                  triggers="hover focus click"
                >
                  <div class="text-left">
                    <ul class="my-0">
                      <li v-if="data.item.merchant_type_name">
                        {{ data.item.merchant_type_name.name }}
                      </li>
                      <div
                        v-for="merchant in data.item.cross_merchants"
                        :key="merchant.id"
                      >
                        <li v-if="merchant.merchant_name">
                          {{ merchant.merchant_name }}
                        </li>
                      </div>
                    </ul>
                  </div>
                </b-popover>
              </template>
              <template v-else>
                <b-badge
                  v-if="data.item.merchant_type_name"
                  variant="primary"
                  class="mr-1"
                  :id="`single-merchant-${data.item.id}`"
                  >{{ data.item.merchant_type_name.type_name }}
                </b-badge>
                <b-popover
                  :target="`single-merchant-${data.item.id}`"
                  triggers="hover focus click"
                >
                  <ul>
                    <li>
                      {{ data.item.merchant_type_name.name }}
                    </li>
                  </ul>
                </b-popover>
                <b-badge
                  v-if="data.item.cross_merchants.length"
                  variant="primary"
                  :id="`cross-merchants-${data.item.id}`"
                >
                  其他通路
                </b-badge>
                <b-popover
                  :target="`cross-merchants-${data.item.id}`"
                  triggers="hover focus click"
                >
                  <div class="text-left">
                    <ul class="my-0">
                      <li
                        v-for="merchant in data.item.cross_merchants"
                        :key="merchant.id"
                      >
                        {{
                          `${merchant.merchant_type_name} (${merchant.merchant_name})`
                        }}
                      </li>
                    </ul>
                  </div>
                </b-popover>
              </template>
            </template>
            <template #cell(actions)="data">
              <b-dropdown variant="secondary">
                <template slot="button-content">
                  <i class="mdi mdi-menu"></i>
                </template>
                <b-dropdown-item
                  :to="{
                    name: 'CustomerDetailView',
                    params: { customerID: data.item.id },
                  }"
                >
                  檢視
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$permissions.has($permissions.consts.CONTACT_LIST_VIEW)"
                  :to="{
                    name: 'CustomerContactList',
                    params: { customerID: data.item.id },
                  }"
                >
                  聯絡人
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>
      </div>

      <CustomPagination
        :currentPage="query.page"
        :totalRows="totalRows"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import store from "@/store";
import { BAvatar } from "bootstrap-vue";
import customerApi from "@/apis/customer";
import moment from "moment";
import axios from "axios";

import "bootstrap-vue/dist/bootstrap-vue.css";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb.vue";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import { paginationMixin } from "@/mixins/pagination";

export default {
  components: { CustomPagination, BAvatar, BreadCrumb },
  mixins: [paginationMixin],
  data() {
    const baseFields = [
      { key: "avatar_url", label: "" },
      { key: "name", label: "顯示名稱" },
      { key: "outer_code", label: "識別編號" },
      {
        key: "created_at",
        label: "加入時間",
        formatter: (value) => {
          return value ? moment(value).format("YYYY-MM-DD HH:mm") : "";
        },
      },
      { key: "merchant_types", label: "來源通路", index: -1 },
    ];

    return {
      totalRows: 0,
      isFetchCustomerList: false,
      bindedOptions: [
        { value: null, text: "無論會員有無綁定" },
        { value: true, text: "已綁定" },
        { value: false, text: "未綁定" },
      ],
      searchFieldOptions: [
        { value: "mobile_phone", text: "手機號碼" },
        { value: "outer_code", text: "識別編號" },
        { value: "name", text: "顯示名稱" },
        { value: "email", text: "Email" },
      ],
      merchants: [],
      customers: [],
      merchant: {},
      baseFields: baseFields,
      fields: baseFields,
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
        search_field: "mobile_phone",
        is_binded: null,
        merchant_hashid: null,
      },
    };
  },

  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
      defaultMerchantType: (state) => state.defaultMerchantType,
    }),
    ...mapGetters({
      getModuleConfig: "dashboardModule/getConfig",
      getModule: "dashboardModule/getModule",
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    searchPlaceholder() {
      const fieldMap = {
        outer_code: "搜尋 識別編號",
        name: "搜尋 顯示名稱",
        mobile_phone: "搜尋 手機號碼",
        email: "搜尋 Email",
      };
      return fieldMap[this.query.search_field] || "搜尋";
    },
  },

  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      const defaultQuery = {
        is_binded: null,
        merchant_hashid: null,
      };
      this.query = { ...defaultQuery, ...to.query };
      this.getCustomerList();
    },
  },

  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    this.init();
  },

  mounted() {
    this.checkAndExpandAdvancedSearch();
  },

  methods: {
    checkAndExpandAdvancedSearch() {
      // 當網址有查詢參數時，自動展開進階搜尋
      const hasQueryParams = Object.keys(this.$route.query).length > 0;
      if (hasQueryParams) {
        this.$nextTick(() => {
          this.$root.$emit('bv::toggle::collapse', 'collapse-filter');
          this.$root.$emit('bv::toggle::collapse', 'collapse-search');
        });
      }
    },
    async init() {
      await this.getMerchants();
      await this.getCustomerList();
      // NOTE: relocate table fields
      this.fields.filter(field => Object.keys(field).includes('index'))
        .map(field => {
          this.fields.splice(this.fields.indexOf(field), 1);
          this.fields.splice(field.index, 0, field);
        })
      this.initialized = true;
    },
    hiddenString(value, length) {
      value = value || "";
      const splittedValue = [...value];
      if (splittedValue.length <= length) {
        return value;
      }
      return splittedValue.slice(0, length).join("") + "...";
    },
    async getMerchants() {
      const { data } = await customerApi.merchantList();
      data.data.forEach((merchant) => {
        if (merchant.is_enabled) {
          this.merchants.push({
            type: merchant.type,
            value: merchant.id,
            text: merchant.type_name
              ? `${merchant.type_name} (${merchant.name})`
              : merchant.name,
          });
        }
      });

      if (!this.query.merchant_hashid) {
        // const defaultMerchant =
        //   data.data?.find(
        //     (merchant) => merchant.type === this.defaultMerchantType
        //   ) ?? data.data?.find((merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE);
        // this.query.merchant_hashid = defaultMerchant.id;
        this.query.merchant_hashid = data.meta.default_id;
      }

      if (this.merchants.length > 1) {
        this.merchants.unshift({ value: "all", text: "全部" });
        this.merchants.unshift({ value: "cross", text: "跨通路" });
      }
      this.merchant = this.merchants.find(
        (merchant) => merchant.value === this.query.merchant_hashid
      );
    },
    async getCustomerList() {
      try {
        this.isFetchCustomerList = true;
        const queryParams = {
          ...this.query,
          merchant_hashid:
            this.query.merchant_hashid === "cross" ||
            this.query.merchant_hashid === "all"
              ? null
              : this.query.merchant_hashid,
          show_cross_merchant:
            this.query.merchant_hashid === "cross" ? true : null,
        };

        const { data } = await customerApi.listV2(queryParams);

        this.fields = this.baseFields
                          .concat(this.getModule("customer") ? [] : this.getDefaultCustomerFields())
                          .concat(this.normalizeProfileFields())
                          .concat({ key: "actions", label: "操作" });
        this.customers = data.data;
        this.totalRows = data.meta.total;
      } catch (e) {
        console.log(e);
      }
      this.isFetchCustomerList = false;
    },
    normalizeProfileFields() {
      const customerList = this.getModuleConfig("customer", "customer_list");

      const profileMappings =
        customerList?.find(
          (list) => list.merchant_type.toUpperCase() == this.merchant.type
        )?.member_fields ?? [];

      console.log(profileMappings);
      if (profileMappings) {
        return profileMappings.map((mapping) => {
          if (mapping.matched_key.startsWith("extra.")) {
            const key = mapping.matched_key.split("extra.")[1];
            return {
              key: `profiles.${key}`,
              label: mapping.title,
            };
          } else {
            return {
              key: mapping.matched_key,
              label: mapping.title,
            };
          }
        });
      } else {
        return [];
      }
    },
    handleChangeMerchant() {
      if (this.query.merchant_hashid === "cross") {
        this.query.is_binded = null;
      }
      this.merchant = this.merchants?.find(
        (merchant) => merchant.value === this.query.merchant_hashid
      );

      this.handlePageReset();
    },
    exportCustomers(type) {
      const merchant_id = this.query.merchant_hashid;
      let baseUrl = `${process.env.VUE_APP_API_BASE_URL}/admin/organizations/${this.organization.id}/customers/export`;
      if (type === "csv") {
        baseUrl = `${process.env.VUE_APP_API_BASE_URL}/admin/organizations/${this.organization.id}/customers/export-csv`;
      }
      let params = new URLSearchParams();
      params.append("token", store.state.auth.token);
      if (merchant_id && merchant_id !== "cross" && merchant_id !== "all") {
        params.append("merchant_hashid", merchant_id);
      }
      if (merchant_id === "cross") {
        params.append("show_cross_merchant", true);
      }
      if (this.query.keyword) {
        params.append("keyword", this.query.keyword);
        if (this.query.search_field) {
          params.append("search_field", this.query.search_field);
        }
      }
      if (this.query.is_binded) {
        params.append("is_binded", this.query.is_binded);
      }
      let url = `${baseUrl}?${params.toString()}`;

      axios.get(url);

      this.$swal({
        title: "成功",
        text: "可至“我的主控台”查看紀錄及下載檔案",
        showCancelButton: true,
        cancelButtonText: '確認',
        type: "success",
        confirmButtonText: "前往查看",
      }).then((result) => {
        if (result.value) {
          this.$router.push({
            name: "MyConsoleDownloadFileList",
            params: { org_code: this.$route.params.org_code },
          });
        }
      });
    },
    getDefaultCustomerFields() {
      return [
        { key: "mobile_phone", label: "手機" },
        { key: "email", label: "Email" },
        {
          key: "birthday",
          label: "生日",
          formatter: (value) => {
            return value ? moment(value).format("YYYY-MM-DD") : "";
          },
        },
      ];
    },
  },
};
</script>

<style></style>
